import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Dispatch, SetStateAction, useCallback, useState } from "react";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import { VariationSelection } from "../../../common/types/apiTypes";
import { AutocompleteType } from "../../../common/types/autoComplete";
import OverlappingOrderRowsError from "../OverlappingOrderRowsError";
import {
  LanguageSkill,
  OrderFields,
  OrderRow,
  ValidationError,
} from "../orderTypes";
import AdditionalInfo from "./AdditionalInfo";
import BasicInfo from "./BasicInfo";
import Descriptions from "./Descriptions";
import OrderShortTesPricingOverride from "./OrderShortTesPricingOverride";
import OrderSupplements from "./OrderSupplements";
import Requirements from "./Requirements";
import SalaryLimit from "./SalaryLimit";
import ShiftDetails from "./ShiftDetails";
import { Skills } from "./Skills";
import TeamNotificationSettings from "./TeamNotificationSettings";
import Terms from "./Terms";
import { AdditionalInfoFormContainer } from "./styles";
import {
  FormValuesChangeValue,
  OrderFieldsProperties,
  OrderTemplateChangeValue,
  ShiftDetailsType,
} from "./types";
import { useAppSelector } from "../../../app/hooks";
import { isExpertCustomerServiceOrAdmin } from "../../user/accessRightChecks";
import { OrderFormShortOtherSettings } from "./OrderFormShortOtherSettings";

type Props = {
  row: OrderRow;
  rowId: string;
  formValues: OrderFields;
  handleFormValuesChange: (name: string, value: FormValuesChangeValue) => void;
  validationErrors: ValidationError[];
  setTesVariation: (variation: VariationSelection[], rowId: string) => void;
  isNewOrder: boolean;
  shiftDetails: ShiftDetailsType[];
  handleShiftDetailsChange: (
    name: string,
    value: Date | null | string | AutocompleteType | boolean,
    id: string,
  ) => void;
  handleError: (error: boolean, errorObject: any) => void;
  setPeriodStartDateIsRequired: Dispatch<SetStateAction<boolean | null>>;
  periodStartDateIsRequired: boolean | null;
};

const OrderAdditionalInfoForm = ({
  rowId,
  formValues,
  handleFormValuesChange,
  validationErrors,
  setTesVariation,
  isNewOrder,
  shiftDetails,
  handleShiftDetailsChange,
  handleError,
  setPeriodStartDateIsRequired,
  periodStartDateIsRequired,
}: Props) => {
  const [tesPricingOverridesOpen, setTesPricingOverridesOpen] = useState(false); // TODO: palkkalajien arvot???

  const { user } = useAppSelector((state) => state.user);
  const { t } = useTranslation(["order", "common"]);

  //Wrapping the name, values so the !bundleRowId ? values.id : undefined logic
  //can be in one place
  const handleChangeCallback = (
    name: OrderFieldsProperties | "isFillShift",
    value: FormValuesChangeValue,
  ) => {
    handleFormValuesChange(name, value);
  };

  const onOrderTemplateChange = useCallback(
    (name: string, value: OrderTemplateChangeValue) => {
      const updatedOrderTemplate = {
        ...formValues.orderTemplate,
        [name]: value,
      };
      handleFormValuesChange("orderTemplate", updatedOrderTemplate);
    },
    [handleFormValuesChange, formValues.orderTemplate],
  );

  const onDescriptionsChange = (
    name: string,
    value: string | null | AutocompleteType,
  ) => {
    const updatedWorkLocation = {
      ...formValues.workLocation,
      [name]: value,
    };
    handleFormValuesChange("workLocation", updatedWorkLocation);
  };

  const onLanguageSkillsChange = (
    name: string,
    languageSkills: LanguageSkill[],
  ) => {
    const orderTemplate = {
      ...formValues.orderTemplate,
      languages: languageSkills,
    };
    handleFormValuesChange("orderTemplate", orderTemplate);
  };

  const onTesVariationChange = (variation: VariationSelection[]) => {
    const orderTemplate = {
      ...formValues.orderTemplate,
      tesVariation: variation,
    };

    setTesVariation(variation, rowId);
    handleFormValuesChange("orderTemplate", orderTemplate);
  };

  const onTeamNotificationSettingsChange = (
    name: "notifyWhenFulfilled" | "notifyMinutesBeforeIfNotFulfilled",
    value: boolean | number,
  ) => {
    if (name === "notifyWhenFulfilled") {
      handleFormValuesChange("notifyWhenFulfilled", value as boolean);
    } else {
      handleFormValuesChange(
        "notifyMinutesBeforeIfNotFulfilled",
        value as number,
      );
    }
  };

  const canViewTeamNotificationSettings = isExpertCustomerServiceOrAdmin(user);
  // for the last FormSection border-bottom logic
  const isLastChild = !canViewTeamNotificationSettings;

  const shiftDetailsLabel =
    shiftDetails && shiftDetails.length > 1
      ? "shiftDetailsPlural"
      : "shiftDetails";

  return (
    <Box sx={{ paddingLeft: "1rem" }}>
      <AdditionalInfoFormContainer>
        <FormSection title={t("orderDetails")}>
          <BasicInfo
            values={formValues}
            handleChangeCallback={handleChangeCallback}
          />
        </FormSection>
        <FormSection xsLabel={3} xsField={9} title={t(shiftDetailsLabel)}>
          <ShiftDetails
            costCenters={formValues.workLocation?.customerCCs}
            handleChangeCallback={handleChangeCallback}
            validationErrors={validationErrors}
            shiftDetailValues={shiftDetails}
            handleShiftDetailsChange={handleShiftDetailsChange}
          />
          <OverlappingOrderRowsError
            validationErrors={validationErrors}
            position={{ left: "420px", bottom: "8px" }}
          />
        </FormSection>
        <FormSection title={t("termsOfEmployment")}>
          <Terms
            values={formValues}
            handleChangeCallback={handleChangeCallback}
            onTesVariationChange={onTesVariationChange}
          />
        </FormSection>
        <FormSection title={t("common:requirements")}>
          <Requirements
            values={formValues}
            onOrderTemplateChange={onOrderTemplateChange}
            onLanguageSkillsChange={onLanguageSkillsChange}
            isNewOrder={isNewOrder}
          />
        </FormSection>
        <FormSection title={t("common:requiredSkills")}>
          <Skills
            values={formValues}
            onOrderTemplateChange={onOrderTemplateChange}
          />
        </FormSection>
        <FormSection title={t("common:description")}>
          <Descriptions
            values={formValues}
            onDescriptionsChange={onDescriptionsChange}
            handleChangeCallback={handleChangeCallback}
          />
        </FormSection>
        <FormSection title={t("order:salaryLimit")}>
          <SalaryLimit
            values={formValues}
            onOrderTemplateChange={onOrderTemplateChange}
            periodStartDateIsRequired={periodStartDateIsRequired}
            handleError={handleError}
          />
        </FormSection>
        <FormSection title={t("common:additionalInfo")}>
          <AdditionalInfo
            values={formValues}
            handleChangeCallback={handleChangeCallback}
          />
        </FormSection>
        <FormSection title={t("talentProfile:showToCustomerSupplement")}>
          <OrderSupplements
            tesId={formValues?.orderTemplate?.tes?.id}
            tesVariation={formValues?.orderTemplate?.tesVariation}
            supplements={formValues.orderTemplate?.supplements || {}}
            onOrderTemplateChange={onOrderTemplateChange}
            editable={true}
            setPeriodStartDateIsRequired={setPeriodStartDateIsRequired}
          />
        </FormSection>
        <FormSection
          title={t("order:salaryCategoryValues")}
          collapse
          noBorder={isLastChild}
          open={tesPricingOverridesOpen}
          toggleOpen={() =>
            setTesPricingOverridesOpen(!tesPricingOverridesOpen)
          }
        >
          <OrderShortTesPricingOverride
            tesId={formValues?.orderTemplate?.tes?.id}
            supplementPriceOverrides={
              formValues.orderTemplate?.supplementPriceOverrides
            }
            onOrderTemplateChange={onOrderTemplateChange}
          />
        </FormSection>
        <OrderFormShortOtherSettings
          isBillable={!!formValues.orderTemplate?.isBillable}
          onIsBillableChange={(value: boolean) =>
            onOrderTemplateChange("isBillable", value)
          }
          tooltipText={t("customers:orderTemplate:notBilledTooltip")}
        />
        {canViewTeamNotificationSettings && (
          <TeamNotificationSettings
            onTeamNotificationSettingsChange={onTeamNotificationSettingsChange}
            validationErrors={validationErrors}
            fieldId={formValues.id}
            shiftDetails={shiftDetails}
            handleError={handleError}
            notifyWhenFulfilled={formValues.notifyWhenFulfilled}
            notifyMinutesBeforeIfNotFulfilled={
              formValues.notifyMinutesBeforeIfNotFulfilled
            }
          />
        )}
      </AdditionalInfoFormContainer>
    </Box>
  );
};

export default OrderAdditionalInfoForm;
